import React from 'react';
import { AlertTriangle, RotateCcw, Home } from 'lucide-react';
import { Button } from './ui/button';

/**
 * App-wide error boundary. Catches render errors in any descendant route
 * and shows a friendly retry screen instead of a blank page.
 *
 * Reset strategy:
 *  - "Try again" clears the error state to re-mount children.
 *  - "Go home" performs a hard navigation to /library so a corrupted
 *    React tree (e.g. broken context) can be fully recreated.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface to the console for diagnostics; production logging hook
    // could be added here later (e.g. Sentry) without changing the UI.
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info?.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    window.location.assign('/library');
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        aria-live="assertive"
        className="min-h-screen flex items-center justify-center px-4"
        style={{ backgroundColor: 'hsl(82, 55%, 97%)' }}
        data-testid="error-boundary-screen"
      >
        <div
          className="max-w-md w-full bg-white rounded-2xl border p-8 text-center shadow-sm"
          style={{ borderColor: 'hsl(143, 28%, 90%)' }}
        >
          <div
            className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'hsl(35, 90%, 95%)' }}
            aria-hidden="true"
          >
            <AlertTriangle className="w-7 h-7" style={{ color: 'hsl(35, 90%, 45%)' }} />
          </div>
          <h1
            className="text-xl font-display font-bold mb-2"
            style={{ color: 'hsl(143, 28%, 25%)' }}
          >
            Something went wrong
          </h1>
          <p className="text-sm mb-6" style={{ color: 'hsl(125, 12%, 35%)' }}>
            Your plants are safe. This screen hit a snag — try again or head back to your library.
          </p>
          {this.state.error?.message && (
            <details
              className="text-left text-xs mb-6 rounded-lg p-3"
              style={{ backgroundColor: 'hsl(82, 55%, 96%)', color: 'hsl(125, 12%, 35%)' }}
            >
              <summary className="cursor-pointer font-medium" data-testid="error-details-toggle">
                Technical details
              </summary>
              <pre className="mt-2 whitespace-pre-wrap break-words" data-testid="error-details">
                {String(this.state.error.message)}
              </pre>
            </details>
          )}
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              onClick={this.handleRetry}
              style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }}
              data-testid="error-retry-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" aria-hidden="true" />
              Try again
            </Button>
            <Button
              variant="outline"
              onClick={this.handleGoHome}
              style={{ borderColor: 'hsl(143, 28%, 80%)', color: 'hsl(143, 28%, 25%)' }}
              data-testid="error-home-btn"
            >
              <Home className="w-4 h-4 mr-2" aria-hidden="true" />
              Go to library
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
